import { useEffect, useMemo, useState } from "react";
import { Plus, RefreshCcw, Search, Trash2, Ban } from "lucide-react";
import useRack from "../../services/Inventory/useRack";
import NoIndex from "../../components/NoIndex";
import LoadingScreen from "../../utils/LoadingScreen";

export default function Racks({ userID }) {
  const { rows, loading, fetchRacks, addRack, deleteRack } = useRack(userID);

  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedRack, setSelectedRack] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const [form, setForm] = useState({
    rackNumber: "",
    category: "",
    tags: [],
    tagInput: "",
  });

  useEffect(() => {
    fetchRacks();
  }, [userID]);

  const filtered = useMemo(() => {
    if (!q) return rows;

    return rows.filter((r) =>
      JSON.stringify(r).toLowerCase().includes(q.toLowerCase()),
    );
  }, [rows, q]);

  const handleAddRack = async () => {
    const payload = {
      userid: userID,
      rackNumber: form.rackNumber,
      category: form.category,
      tags: form.tags,
    };

    const success = await addRack(payload);

    if (success) {
      setOpen(false);
      setForm({
        rackNumber: "",
        category: "",
        tags: [],
        tagInput: "",
      });
    }
  };

  const confirmDelete = async () => {
    if (!selectedRack) return;

    await deleteRack(selectedRack.id);

    setDeleteModal(false);
    setSelectedRack(null);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchRacks();
    setRefreshing(false);
  };

  return (
    <>
    
    <div className="p-6 min-h-0 h-full flex flex-col overflow-hidden montserrat-font-1">
      {/* HEADER */}
      <NoIndex />
      <div className="flex items-center justify-between mb-6 montserrat-font-1">
        <h2 className="text-xl font-semibold text-gray-800">Rack Management</h2>

        <div className="flex items-center gap-3 montserrat-font-1">
          {/* Search */}

          <div className="flex items-center bg-white border rounded-lg px-3 py-2 shadow-sm montserrat-font-1">
            <Search size={16} className="text-gray-400" />
            <input
              className="ml-2 outline-none text-sm montserrat-font-1"
              placeholder="Search by rack #, category, tags, status"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>

          {/* Add */}

          <button
            onClick={() => setOpen(true)}
            className="flex items-center gap-2 montserrat-font-1 bg-emerald-500 hover:bg-emerald-600 text-white text-sm px-4 py-2 rounded-lg shadow"
          >
            <Plus size={16} />
            Add Rack
          </button>

          {/* Refresh */}

          <button
            onClick={handleRefresh}
            className="px-3 py-1.5 bg-blue-500 text-white border rounded-lg montserrat-font-1 shadow-sm flex gap-2 items-center "
          >
            <RefreshCcw
              size={16}
              className={refreshing ? "animate-spin" : ""}
            />
            Refresh
          </button>
        </div>
      </div>

      {/* TABLE CARD */}

      <div className="bg-white rounded-xl border shadow-sm overflow-auto h-full montserrat-font-1">
        {/* Table Header */}

        <div className="sticky top-0 z-10 grid grid-cols-5 text-sm font-medium text-gray-800 bg-green-100 px-6 py-3 montserrat-font-1">
          <div>Rack #</div>
          <div>Category</div>
          <div>Tags</div>
          <div>Status</div>
          <div className="text-right">Actions</div>
        </div>

        {/* Table Body */}

        {(loading || refreshing) && (
          <div className="flex items-center justify-center h-40">
            <LoadingScreen text="Refreshing racks..." />
          </div>
        )}

        {!(loading || refreshing) &&
          filtered.map((r, index) => {
            const tags = Array.isArray(r.tags)
              ? r.tags
              : JSON.parse(r.tags || "[]");

            const active = r.status === "Y";

            return (
              <div
                key={r.id}
                className="grid grid-cols-5 items-center px-6 py-4 text-sm border-t hover:bg-gray-50 montserrat-font-1"
              >
                <div className="text-gray-800 font-medium montserrat-font-1">{index + 1}</div>

                <div>{r.category}</div>

                <div>{tags.length ? tags.join(", ") : "—"}</div>

                <div>
                  {active ? (
                    <span className="inline-flex items-center gap-1 text-xs font-medium bg-green-100 text-green-700 px-3 py-1 rounded-full">
                      Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs font-medium bg-red-100 text-red-600 px-3 py-1 rounded-full">
                      <Ban size={12} />
                      Inactive
                    </span>
                  )}
                </div>

                <div className="flex justify-end montserrat-font-1">
                  <button
                    onClick={() => {
                      setSelectedRack(r);
                      setDeleteModal(true);
                    }}
                    className="p-2 border rounded-lg text-red-500 hover:bg-red-50"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            );
          })}
      </div>

      {/* DRAWER */}

      {open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 montserrat-font-1">
          {/* Drawer */}
          <div className="w-[420px] bg-white max-h-[90vh] shadow-xl flex flex-col rounded-2xl montserrat-font-1">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b montserrat-font-1">
              <h3 className="text-lg font-semibold text-gray-800">Add Rack</h3>

              <button
                onClick={() => setOpen(false)}
                className="w-9 h-9 flex items-center justify-center rounded-lg border hover:bg-gray-100 montserrat-font-1"
              >
                ✕
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5 montserrat-font-1">
              {/* Rack Number */}
              <div className="montserrat-font-1">
                <label className="text-sm font-medium text-gray-700">
                  Rack Number <span className="text-red-500">*</span>
                </label>

                <input
                  type="text"
                  placeholder="e.g., 1, A-01, B-12"
                  className="mt-2 w-full border rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-emerald-500 outline-none"
                  value={form.rackNumber}
                  onChange={(e) =>
                    setForm({ ...form, rackNumber: e.target.value })
                  }
                />
              </div>

              {/* Category */}
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Category
                </label>

                <input
                  type="text"
                  placeholder="e.g., grocery"
                  className="mt-2 w-full border rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-emerald-500 outline-none"
                  value={form.category}
                  onChange={(e) =>
                    setForm({ ...form, category: e.target.value })
                  }
                />
              </div>

              {/* Tags */}
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Tags
                </label>

                <input
                  type="text"
                  placeholder="Type and press Enter"
                  className="mt-2 w-full border rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-emerald-500 outline-none"
                />

                <p className="text-xs text-gray-500 mt-2">
                  Tip: Press <b>Enter</b> or <b>,</b> to add. Paste
                  comma-separated list to add many.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t px-6 py-4 flex justify-end gap-3 montserrat-font-1">
              <button
                onClick={() => setOpen(false)}
                className="px-5 py-2 rounded-lg border text-gray-700 hover:bg-gray-100 montserrat-font-1"
              >
                Cancel
              </button>

              <button
                onClick={handleAddRack}
                className="px-5 py-2 rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 montserrat-font-1"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* delete modal */}

      {deleteModal && selectedRack && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 montserrat-font-1">
          <div className="bg-white w-[420px] rounded-xl shadow-lg p-6 montserrat-font-1">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Delete Rack
            </h3>

            <p className="text-sm text-gray-600 mb-4 montserrat-font-2">
              Are you sure you want to delete this rack?
            </p>

            <div className="bg-gray-50 border rounded-lg p-4 mb-5 text-sm montserrat-font-1">
              <div className="flex justify-between mb-1">
                <span className="text-gray-500">Rack Number</span>
                <span className="font-medium">{selectedRack.rackNumber}</span>
              </div>

              <div className="flex justify-between mb-1">
                <span className="text-gray-500">Category</span>
                <span>{selectedRack.category}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-500">Tags</span>
                <span>
                  {selectedRack.tags?.length
                    ? selectedRack.tags.join(", ")
                    : "—"}
                </span>
              </div>
            </div>

            <div className="flex justify-end gap-3 montserrat-font-1">
              <button
                onClick={() => {
                  setDeleteModal(false);
                  setSelectedRack(null);
                }}
                className="px-4 py-2 border rounded-lg"
              >
                Cancel
              </button>

              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
              >
                Confirm Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
}
